import { isMobile } from "@utils/deviceCapabilities";
import { isMobileDynamic } from "@utils/isMobileDynamic";
import { waitForElement } from "@utils/waitForElement";
import debounce from "lodash/debounce";
import { handleCustomUDL } from "@utils/UDLUtils";

declare const s7viewers: any;
declare const YT: any;

let parseModsPatched = false;

function patchParseMods(): void {
  if (parseModsPatched) return;
  if (typeof s7viewers === "undefined" || !s7viewers.VideoViewer) return;
  parseModsPatched = true;
  const oldParseMods = s7viewers.VideoViewer.prototype.parseMods;
  s7viewers.VideoViewer.prototype.parseMods = function () {
    if (typeof this.params.apiparameters !== "undefined") {
      let vars = this.params.apiparameters.split("&");
      for (const pair of vars) {
        const [rawKey, rawValue] = pair.split("=");
        if (!rawKey) continue;
        const key = decodeURIComponent(rawKey).trim();
        if (!key || rawValue === undefined) continue;
        const value = decodeURIComponent(rawValue).trim();
        this.s7params.params[key] = value;
      }
    }
    return oldParseMods.apply(this, arguments);
  };
}
patchParseMods();

// Global YouTube IFrame API callback
(window as any).onYouTubeIframeAPIReady = function () {
  document.dispatchEvent(new CustomEvent("youtubeVideoApiReady", { detail: { youtube: YT } }));
};

export class Movie {
  private ctx: HTMLElement;
  private movieCanvas: HTMLElement | null;
  private movieCanvasId: string;
  private movieType: string;
  private movieId: string;
  private startOffset: string;
  private autoPlay: boolean;
  private isMute: boolean;
  private apiParameters: string;
  private isBackgroundMovie: boolean;
  private isVideoBasedRatio: boolean;
  private movieWrapper: HTMLElement | null;
  private section: HTMLElement | null;
  private videoBasedRatio: number | null = null;
  private currentDevice: string;
  private player: any = null;
  private youtube: any = null;
  private videoElement: HTMLVideoElement | null = null;
  private vimeoIframe: HTMLIFrameElement | null = null;
  private abortController = new AbortController();
  private intersectionObserver: IntersectionObserver | null = null;
  private inViewport = false;

  constructor(element: HTMLElement) {
    this.ctx = element;
    this.movieCanvas = this.ctx.querySelector(".js-movie-canvas");
    this.movieCanvasId = this.movieCanvas?.id ?? "";
    this.movieType = this.movieCanvas?.dataset.movieType ?? "";
    this.movieId = this.movieCanvas?.dataset.movieId ?? "";
    this.startOffset = this.movieCanvas?.dataset.startOffset ?? "";
    this.autoPlay = this.movieCanvas?.dataset.autoPlay === "true" || this.movieCanvas?.dataset.autoPlay === "1";
    this.isMute = this.movieCanvas?.dataset.mute === "true";
    this.apiParameters = this.movieCanvas?.dataset.apiParameters ?? "";
    this.isBackgroundMovie = this.ctx.classList.contains("js-is-background-video");
    this.isVideoBasedRatio = this.ctx.classList.contains("video-based-ratio");
    this.movieWrapper = this.ctx.querySelector(".movie-wrapper");
    this.section = this.ctx.closest(".comp-section");
    this.currentDevice = this.getCurrentDevice();
  }

  init(): this {
    if (this.ctx.parentElement?.classList.contains("no-initialize")) return this;

    const { signal } = this.abortController;

    if (this.movieType === "dynamicmedia") {
      document.addEventListener("tabsChanged", this.reloadDynamicMediaVideo.bind(this), { signal });

      const accordionDetails = this.ctx.closest<HTMLDetailsElement>(".comp-accordion details");
      if (accordionDetails) {
        accordionDetails.addEventListener("toggle", () => {
          if (accordionDetails.open) this.reloadDynamicMediaVideo(null);
        }, { signal });
      }

      window.addEventListener("resize", debounce(() => {
        if (this.deviceHasChanged() && this.isBackgroundMovie) {
          this.reloadDynamicMediaVideo(null);
        }
      }, 200), { signal });

      this.loadDynamicMediaVideo();
    }

    if (this.movieType === "vimeo") {
      this.loadVimeoVideo();
    }

    if (this.movieType === "youtube") {
      this.player = null;
      if (this.movieCanvas) {
        if (!(window as any).YT) {
          this.loadYoutubeVideoApi();
        } else {
          this.onYoutubeVideoApiReady(null, { youtube: (window as any).YT });
        }
      }
      document.addEventListener("youtubeVideoApiReady", (e: Event) => {
        this.onYoutubeVideoApiReady(e, (e as CustomEvent).detail);
      }, { signal });
    }

    if (this.movieType === "youku") {
      this.loadYouKuVideo();
    }

    if (this.isBackgroundMovie) {
      this.setupBackgroundPlayback();
    }

    return this;
  }

  destroy(): void {
    this.abortController.abort();
    this.intersectionObserver?.disconnect();
  }

  private reloadDynamicMediaVideo(e: Event | null): void {
    const data = (e as CustomEvent)?.detail;
    if (data && data.id === this.ctx.closest(data.selector)?.id) {
      this.loadDynamicMediaVideo();
    } else if (!data) {
      this.loadDynamicMediaVideo();
    }
  }

  private getCurrentDevice(): string {
    return isMobile() || isMobileDynamic() ? "mobile" : "desktop";
  }

  private deviceHasChanged(): boolean {
    const newDevice = this.getCurrentDevice();
    return newDevice !== this.currentDevice;
  }

  private loadDynamicMediaVideo(): void {
    const ctx = this.ctx as any;
    if (
      this.ctx.offsetParent !== null &&
      ctx.dynamicMediaPlayerInstance === undefined &&
      !this.ctx.querySelector("video")
    ) {
      const container = this.ctx.querySelector<HTMLElement>(`#container_${this.movieCanvasId}`);
      if (!container) return;

      let apiParameters = this.apiParameters;
      if (this.autoPlay || this.isBackgroundMovie) apiParameters = "autoplay=1&" + apiParameters;
      if (this.isMute || this.isBackgroundMovie) apiParameters = "mutevolume=1&" + apiParameters;
      if (this.isBackgroundMovie) apiParameters = "loop=1&" + apiParameters;

      const createViewer = () => {
        patchParseMods();
        ctx.dynamicMediaPlayerInstance = new s7viewers.VideoViewer({
          containerId: `container_${this.movieCanvasId}`,
          params: {
            serverurl: container.dataset.serverurl,
            contenturl: container.dataset.contenturl,
            posterimage: container.dataset.posterimage,
            config: container.dataset.config,
            videoserverurl: container.dataset.videoserverurl,
            asset: container.dataset.asset,
            apiparameters: apiParameters,
            ...(container.dataset.initialbitrate && {
              "VideoPlayer.initialbitrate": container.dataset.initialbitrate,
            }),
            ...(container.dataset.progressivebitrate && {
              "VideoPlayer.progressivebitrate": container.dataset.progressivebitrate,
            }),
          },
        });
        ctx.dynamicMediaPlayerInstance.init();

        if (this.isBackgroundMovie) {
          waitForElement(`#container_${this.movieCanvasId} video`).then((elm) => {
            this.videoElement = elm as HTMLVideoElement | null;
            // autoplay=1 starts the video loading; pause immediately if off-screen
            if (!this.inViewport) this.videoElement?.pause();
          });
        }

        waitForElement(`#container_${this.movieCanvasId} video`).then((elm) =>
          this.handleUDLs(elm as HTMLVideoElement, container.dataset.asset ?? ""),
        );

        if (navigator.userAgent.includes("iPhone")) {
          waitForElement(`.lightbox__dialog-content #container_${this.movieCanvasId} video`).then((elm) => {
            (elm as HTMLElement).removeAttribute("playsinline");
          });
        }

        if (this.isVideoBasedRatio && !this.isBackgroundMovie) {
          waitForElement(`#container_${this.movieCanvasId} video`).then((elm) => {
            const video = elm as HTMLVideoElement;
            const videoBasedRatio = video.offsetWidth / video.offsetHeight;
            if (window.innerWidth < 767 && this.movieWrapper) {
              this.movieWrapper.style.aspectRatio = String(videoBasedRatio);
            }
            window.addEventListener("resize", () => {
              if (!this.movieWrapper) return;
              this.movieWrapper.style.aspectRatio = window.innerWidth < 544 ? String(videoBasedRatio) : "";
            }, { signal: this.abortController.signal });
          });
        }

        const setAspectRatioIfRatioChanged = (ratio: number) => {
          if (ratio !== this.videoBasedRatio) {
            this.videoBasedRatio = ratio;
            if (this.section) this.section.style.setProperty("--video-aspect-ratio", String(ratio));
          }
        };

        const handleVideoBasedRatio = (elm: HTMLVideoElement) => {
          const w = elm.videoWidth || elm.offsetWidth;
          const h = elm.videoHeight || elm.offsetHeight;
          if (h <= 0) return;
          setAspectRatioIfRatioChanged(Math.round(w) / Math.round(h));
        };

        if (this.isVideoBasedRatio && this.isBackgroundMovie) {
          waitForElement(`#container_${this.movieCanvasId} video`).then((elm) => {
            const video = elm as HTMLVideoElement;
            video.addEventListener("playing", () => handleVideoBasedRatio(video));
            window.addEventListener("resize", debounce(() => handleVideoBasedRatio(video), 100), {
              signal: this.abortController.signal,
            });
            handleVideoBasedRatio(video);
          });

          waitForElement(`#container_${this.movieCanvasId} video.s7videoelement`).then((elm) => {
            const video = elm as HTMLVideoElement;
            window.addEventListener("resize", debounce(() => handleVideoBasedRatio(video), 100), {
              signal: this.abortController.signal,
            });
            handleVideoBasedRatio(video);
          });
        }
      };

      if (typeof s7viewers !== "undefined" && s7viewers.VideoViewer) {
        createViewer();
      } else {
        const script = document.getElementById("s7-video-viewer-js");
        if (script) {
          const onLoad = () => {
            script.removeEventListener("load", onLoad);
            createViewer();
          };
          script.addEventListener("load", onLoad);
        }
      }
    }
  }

  private handleUDLs(videoElement: HTMLVideoElement, assetName: string): void {
    if (this.isBackgroundMovie) return;

    const title = assetName.split("\\").pop()!.split("/").pop()!;

    const pushData = (eventName: string, percentage?: number) => {
      handleCustomUDL(eventName, {
        title,
        ...(percentage !== undefined && { percentage }),
      });
    };

    videoElement.addEventListener("play", () => pushData("video_start"));
    videoElement.addEventListener("ended", () => pushData("video_complete"));

    const trackedPercentages: number[] = [];
    videoElement.addEventListener("timeupdate", () => {
      const duration = videoElement.duration;
      if (!Number.isFinite(duration) || duration <= 0) return;
      const pct = Math.floor((100 / duration) * videoElement.currentTime);
      let mark: number | undefined;
      if (pct >= 25 && pct < 50 && !trackedPercentages.includes(25)) mark = 25;
      else if (pct >= 50 && pct < 75 && !trackedPercentages.includes(50)) mark = 50;
      else if (pct >= 75 && pct < 100 && !trackedPercentages.includes(75)) mark = 75;
      if (mark !== undefined) {
        trackedPercentages.push(mark);
        pushData("video_progress", mark);
      }
    });
  }

  private loadYoutubeVideoApi(): void {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    const first = document.getElementsByTagName("script")[0];
    if (first?.parentNode) {
      first.parentNode.insertBefore(tag, first);
    } else {
      document.head.appendChild(tag);
    }
  }

  private onYoutubeVideoApiReady(_event: Event | null, data: { youtube: any }): void {
    this.youtube = data?.youtube;
    if (!this.youtube) return;

    const playerOptions: any = {
      width: "100%",
      height: "100%",
      videoId: this.movieId,
      wmode: "transparent",
      enablejsapi: 1,
      playerVars: {
        wmode: "transparent",
        autoplay: this.autoPlay ? 1 : 0,
        mute: this.isMute ? 1 : 0,
        start: this.startOffset || 0,
      },
      events: { onReady: this.onYoutubeVideoPlayerReady.bind(this) },
    };

    if (this.ctx.closest(".comp-card-movie-overlay-inner")) return;

    if (this.apiParameters) {
      for (const paramPair of this.apiParameters.split("&")) {
        if (!paramPair) continue;
        const [paramName, paramValue] = paramPair.split("=");
        const numValue = parseInt(paramValue, 10);
        playerOptions.playerVars[paramName] = isNaN(numValue) ? paramValue : numValue;
      }
    }

    if (this.isBackgroundMovie) {
      Object.assign(playerOptions.playerVars, {
        mute: 1, controls: 0, modestbranding: 1,
        iv_load_policy: 3, loop: 1, rel: 0, showinfo: 0,
        enablejsapi: 1, playlist: this.movieId,
      });
    }

    this.player = new this.youtube.Player(this.movieCanvasId, playerOptions);
  }

  private onYoutubeVideoPlayerReady(): void {
    this.ctx.classList.remove("pre-initialized");
    if (this.isMute) this.muteVideo();
    if (this.isBackgroundMovie) {
      if (this.inViewport) {
        this.player?.playVideo();
      } else {
        this.player?.pauseVideo();
      }
    }
  }

  private loadVimeoVideo(): void {
    if (this.movieCanvas?.querySelector("iframe")) return;

    const { signal } = this.abortController;
    const iframe = document.createElement("iframe");
    let url = `https://player.vimeo.com/video/${this.movieId}?api=1&player_id=${this.movieCanvasId}`;

    if (this.isBackgroundMovie) url += "&loop=1";
    if (this.apiParameters) url += `&${this.apiParameters}`;
    if (this.autoPlay) { url += "&autoplay=1"; iframe.setAttribute("allow", "autoplay"); }
    if (this.isMute) url += "&muted=1";
    if (this.isBackgroundMovie) url += "&background=1";
    if (this.startOffset) url += `#t=${this.startOffset}`;

    iframe.id = this.movieCanvasId;
    iframe.className = "vimeo-iframe";
    iframe.src = url;
    iframe.setAttribute("frameborder", "0");
    if (!/allowfullscreen=(0|false)/.test(url)) iframe.setAttribute("allowfullscreen", "true");
    iframe.style.width = "100%";
    iframe.style.height = "100%";

    this.movieCanvas?.appendChild(iframe);

    if (this.isBackgroundMovie) {
      this.vimeoIframe = iframe;
      window.addEventListener("message", (e) => {
        if (e.origin !== "https://player.vimeo.com") return;
        try {
          const data = JSON.parse(typeof e.data === "string" ? e.data : "{}");
          if (data.player_id === this.movieCanvasId && data.event === "ready") {
            if (!this.inViewport) this.sendVimeoCommand("pause");
          }
        } catch { /* ignore malformed messages */ }
      }, { signal });
    }
  }

  private sendVimeoCommand(method: string): void {
    this.vimeoIframe?.contentWindow?.postMessage(
      JSON.stringify({ method }),
      "https://player.vimeo.com",
    );
  }

  private loadYouKuVideo(): void {
    if (this.movieCanvas?.querySelector("iframe")) return;

    const iframe = document.createElement("iframe");
    let url = `https://player.youku.com/embed/${this.movieId}==?autoplay=${this.autoPlay ? "true" : "false"}`;
    if (this.apiParameters) url += `&${this.apiParameters}`;
    if (this.autoPlay) iframe.setAttribute("allow", "autoplay");

    iframe.id = this.movieCanvasId;
    iframe.className = "youku-iframe";
    iframe.src = url;
    iframe.setAttribute("frameborder", "0");
    iframe.setAttribute("allowfullscreen", "1");
    iframe.style.width = "100%";
    iframe.style.height = "100%";

    this.movieCanvas?.appendChild(iframe);
  }

  private setupBackgroundPlayback(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.inViewport = entry.isIntersecting;
          if (entry.isIntersecting) {
            this.playBackgroundVideo();
          } else {
            this.pauseBackgroundVideo();
          }
        }
      },
      { threshold: 0 },
    );
    this.intersectionObserver.observe(this.ctx);
  }

  private playBackgroundVideo(): void {
    if (this.movieType === "dynamicmedia") {
      this.videoElement?.play().catch(() => {});
    } else if (this.movieType === "youtube") {
      this.player?.playVideo();
    } else if (this.movieType === "vimeo") {
      this.sendVimeoCommand("play");
    }
  }

  private pauseBackgroundVideo(): void {
    if (this.movieType === "dynamicmedia") {
      this.videoElement?.pause();
    } else if (this.movieType === "youtube") {
      this.player?.pauseVideo();
    } else if (this.movieType === "vimeo") {
      this.sendVimeoCommand("pause");
    }
  }

  private muteVideo(): void {
    if (this.movieType === "youtube") this.player?.mute();
  }

  static initAll(): void {
    document
      .querySelectorAll<HTMLElement>('[data-component="movie"]')
      .forEach((el) => new Movie(el).init());
  }

  static initOnElement(element: HTMLElement): void {
    if (!element) return;

    if (element.dataset.component === "movie") {
      new Movie(element).init();
    } else {
      element.querySelectorAll<HTMLElement>('[data-component="movie"]').forEach((el) => new Movie(el).init());
    }
  }
}
